<template>
  <Preloader v-if="loading" />
  <VRow v-else-if="errorMessage">
    <VCol cols="12">
      <VAlert type="error">
        {{ errorMessage }}
      </VAlert>
    </VCol>
  </VRow>
  <VRow v-else>
    <VCol cols="12" v-if="vacancy">
      <VacancyStepTitle
        v-if="vacancy"
        :progress="60"
        :vacancy="vacancy"
        :step="3"
      >
        {{ $t('step') }}
      </VacancyStepTitle>
      <div class="mt-2 mt-md-0">
        <a @click="goToWizard" class="link-dotted">
          {{ $t('returnToTemplates') }}
        </a>
      </div>
      <VacancyQuestionsForm
        :vacancy="vacancy"
        :questions="questions"
        :categories="categories"
        :selected="selected"
        @submit="setSelectedQuestions"
        :interviewer-id="interviewerId"
      >
        <template v-slot:buttons="{ disabled, submit }">
          <div class="d-flex">
            <VBtn
              dark
              color="success"
              outlined
              class="mr-auto mr-md-2"
              elevation="0"
              @click="back"
            >
              {{ $t('goBack') }}
            </VBtn>
            <VBtn
              color="success"
              type="submit"
              :dark="!disabled"
              :disabled="disabled"
              :loading="loading"
              @click="next(submit)"
              elevation="0"
            >
              {{ $t('next') }} &rarr;
            </VBtn>
          </div>
        </template>
      </VacancyQuestionsForm>
    </VCol>
  </VRow>
</template>

<script>
import VacancyStepTitle from '@/views/Vacancy/VacancyStepTitle';
import Wizard from '@/orm/models/Wizard/Wizard';
import { wizardSteps } from '@/enums';
import ValidationMixin from '../../mixins/ValidationMixin';
import VacancyQuestionsForm from '../../components/Vacancy/VacancyQuestionsForm.vue';
import Preloader from '../../components/Preloader.vue';
import { safeGet } from '../../functions';
import Vacancy from '../../orm/models/Vacancy/Vacancy';
import InterviewVideoQuestionVideo from '../../orm/models/Interview/InterviewVideoQuestionVideo';
import InterviewVideoCategoryVideo from '../../orm/models/Interview/InterviewVideoCategoryVideo';

export default {
  name: 'VacancyStepQuestions',
  metaInfo() {
    return {
      title: this.$t('step'),
    };
  },
  components: {
    VacancyStepTitle,
    Preloader,
    VacancyQuestionsForm,
  },
  mixins: [ValidationMixin],
  props: {
    vacancyId: {
      type: Number,
      required: true,
    },
    interviewerId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      isActivePreset: !!this.$route.query.activePreset,
    };
  },
  computed: {
    vacancy() {
      return Vacancy.query().find(this.vacancyId);
    },
    vacancyQuestions() {
      return safeGet(this.vacancy, ['script', 'questions'], []);
    },
    selected() {
      return Wizard.modelState().selectedQuestions.map((question) => {
        return this.questions.find((q) => q.questionId === question.questionId) || question;
      });
    },
    questions() {
      return InterviewVideoQuestionVideo.query().withAllRecursive().all();
    },
    categories() {
      return InterviewVideoCategoryVideo.query().where('depth', 0).withAllRecursive().all();
    },
  },
  created() {
    this.prepare();
  },
  methods: {
    setState() {
      Wizard
        .setVacancyId(this.vacancyId)
        .setInterviewerId(this.interviewerId)
        .setWizardStep(wizardSteps.QUESTIONS);
    },
    async prepare() {
      this.setState();
      await this.withLoading(async () => {
        await Promise.all([
          Wizard.fetchData(),
        ]);
      }, 'loading');
    },
    back() {
      Wizard.pushPrevRoute(this.$router);
    },
    next(submit) {
      submit();
      this.withLoading(async () => {
        await Wizard.saveWizardState();
        await Wizard.pushNextRoute(this.$router);
      });
    },
    setSelectedQuestions(selected) {
      Wizard.setSelectedQuestions(selected);
    },
    async goToWizard() {
      await this.$router.push({
        name: 'vacancy-preset',
        params: {
          vacancyId: this.vacancyId,
        },
      });
    },
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => vm.setState());
  },
  beforeRouteUpdate(to, from, next) {
    this.setState();
    next();
  },
};
</script>

<i18n locale="en" lang="yaml">
  step: "interview questions list"
  next: Next
  lostInfoWarning: Changes not saved will be lost. Are you sure?
  continue: Continue editing
  goBack: Go back
  returnToMaster: Back to the Job question selection
  returnToTemplates: Go back to the Template Selection Wizard
</i18n>

<i18n locale="ru" lang="yaml">
  step: "список вопросов для интервью"
  next: Далее
  lostInfoWarning: Все изменения будут потеряны. Вы уверены?
  continue: Продолжить редактирование
  goBack: Назад
  returnToMaster: Вернуться в мастер подбора вопросов
  returnToTemplates: Вернуться в мастер выбора шаблона
</i18n>
